import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';

// Async thunk for applying physics to objects
export const applyPhysicsToObject = createAsyncThunk(
  'physics/applyPhysicsToObject',
  async (data) => {
    return data;
  }
);

// Async thunk for removing physics from objects
export const removePhysicsFromObject = createAsyncThunk(
  'physics/removePhysicsFromObject',
  async (objectId) => {
    return objectId;
  }
);

// Async thunk for updating physics properties
export const updatePhysicsProperties = createAsyncThunk(
  'physics/updatePhysicsProperties',
  async (data) => {
    return data;
  }
);

// Async thunk for enabling/disabling physics world
export const togglePhysicsWorld = createAsyncThunk(
  'physics/togglePhysicsWorld',
  async (enabled) => {
    return enabled;
  }
);

const initialState = {
  // Physics world settings
  physicsEnabled: false,
  worldSettings: {
    gravity: { x: 0, y: -9.82, z: 0 },
    broadphase: 'NaiveBroadphase',
    solver: 'GSSolver',
    iterations: 10,
    tolerance: 0.1,
  },

  // Physics objects in the scene
  physicsObjects: [],

  // Default physics properties (aligned with MongoDB schema)
  defaultPhysicsProperties: {
    mass: 5, // Matches MongoDB schema default
    linearDamping: 0.1, // Matches MongoDB schema default
    angularDamping: 0.1, // Matches MongoDB schema default
    automaticCenterOfMass: true,
    centerOfMass: { x: 0, y: 0, z: 0 },
    automaticTensor: true,
    inertiaTensor: { x: 1, y: 1, z: 1 },
    inertiaTensorRotation: { x: 0, y: 0, z: 0 },
    useGravity: true,
    isKinematic: false,
    interpolate: 'None',
    collisionDetection: 'Discrete',
    freezePosition: { x: false, y: false, z: false },
    freezeRotation: { x: false, y: false, z: false },
    includeLayers: 'Nothing',
    excludeLayers: 'Nothing',
    colliderType: 'Box', // Matches MongoDB schema default
    isViewCollider: true,
    physicsMaterial: {
      name: 'Default', // Matches MongoDB schema structure
      friction: 0.5,
      restitution: 0.7,
      frictionEquation: 'Default',
    },
  },

  // Physics materials library
  physicsMaterials: {
    Default: { friction: 0.3, restitution: 0.3 },
    Ice: { friction: 0.1, restitution: 0.1 },
    Rubber: { friction: 0.8, restitution: 0.8 },
    Metal: { friction: 0.4, restitution: 0.1 },
    Wood: { friction: 0.6, restitution: 0.2 },
    Concrete: { friction: 0.7, restitution: 0.1 },
  },

  // Collider types
  colliderTypes: [
    { value: 'Box', label: 'Box' },
    { value: 'Sphere', label: 'Sphere' },
    { value: 'Capsule', label: 'Capsule' },
    { value: 'Cylinder', label: 'Cylinder' },
    { value: 'Mesh', label: 'Mesh' },
    { value: 'Plane', label: 'Plane' },
  ],

  // Loading states
  loading: false,
  error: null,

  // Preview mode physics state
  previewModePhysics: {
    enabled: false,
    objects: [],
    world: null,
  },
};

const physicsSlice = createSlice({
  name: 'physics',
  initialState,
  reducers: {
    // Set physics properties for a specific object
    setObjectPhysicsProperties: (state, action) => {
      const { objectId, properties } = action.payload;
      const existingIndex = state.physicsObjects.findIndex(
        (obj) => obj.objectId === objectId
      );

      if (existingIndex >= 0) {
        state.physicsObjects[existingIndex] = {
          ...state.physicsObjects[existingIndex],
          ...properties,
        };
      } else {
        state.physicsObjects.push({
          objectId,
          ...state.defaultPhysicsProperties,
          ...properties,
        });
      }
    },

    // Remove physics from an object
    removeObjectPhysics: (state, action) => {
      const objectId = action.payload;
      state.physicsObjects = state.physicsObjects.filter(
        (obj) => obj.objectId !== objectId
      );
    },

    // Update world settings
    updateWorldSettings: (state, action) => {
      state.worldSettings = { ...state.worldSettings, ...action.payload };
    },

    // Set physics enabled state
    setPhysicsEnabled: (state, action) => {
      state.physicsEnabled = action.payload;
    },

    // Update default physics properties
    updateDefaultPhysicsProperties: (state, action) => {
      state.defaultPhysicsProperties = {
        ...state.defaultPhysicsProperties,
        ...action.payload,
      };
    },

    // Add physics material
    addPhysicsMaterial: (state, action) => {
      const { name, properties } = action.payload;
      state.physicsMaterials[name] = properties;
    },

    // Update physics material
    updatePhysicsMaterial: (state, action) => {
      const { name, properties } = action.payload;
      if (state.physicsMaterials[name]) {
        state.physicsMaterials[name] = {
          ...state.physicsMaterials[name],
          ...properties,
        };
      }
    },

    // Preview mode physics actions
    setPreviewModePhysicsEnabled: (state, action) => {
      state.previewModePhysics.enabled = action.payload;
    },

    setPreviewModePhysicsWorld: (state, action) => {
      state.previewModePhysics.world = action.payload;
    },

    addPreviewModePhysicsObject: (state, action) => {
      const { objectId, physicsBody } = action.payload;
      const existingIndex = state.previewModePhysics.objects.findIndex(
        (obj) => obj.objectId === objectId
      );

      if (existingIndex >= 0) {
        state.previewModePhysics.objects[existingIndex] = {
          objectId,
          physicsBody,
        };
      } else {
        state.previewModePhysics.objects.push({ objectId, physicsBody });
      }
    },

    removePreviewModePhysicsObject: (state, action) => {
      const objectId = action.payload;
      state.previewModePhysics.objects =
        state.previewModePhysics.objects.filter(
          (obj) => obj.objectId !== objectId
        );
    },

    clearPreviewModePhysics: (state) => {
      state.previewModePhysics.objects = [];
      state.previewModePhysics.world = null;
    },

    // Clear all physics data
    clearAllPhysics: (state) => {
      state.physicsObjects = [];
      state.previewModePhysics.objects = [];
      state.previewModePhysics.world = null;
    },

    // Set loading state
    setPhysicsLoading: (state, action) => {
      state.loading = action.payload;
    },

    // Set error state
    setPhysicsError: (state, action) => {
      state.error = action.payload;
    },

    // Load physics data from backend (for project loading)
    loadPhysicsData: (state, action) => {
      const { physicsData, worldSettings } = action.payload;

      if (Array.isArray(physicsData)) {
        // Transform backend format to frontend format
        state.physicsObjects = physicsData.map((item) => {
          // Ensure physicsMaterial has correct structure
          const physicsMaterial = item.physicsMaterial || {};
          const material = {
            name: physicsMaterial.name || 'Default',
            friction: physicsMaterial.friction ?? 0.5,
            restitution: physicsMaterial.restitution ?? 0.7,
            frictionEquation: physicsMaterial.frictionEquation || 'Default',
          };

          return {
            objectId: item.objectId,
            mass: item.mass ?? 5,
            linearDamping: item.linearDamping ?? 0.1,
            angularDamping: item.angularDamping ?? 0.1,
            automaticCenterOfMass: item.automaticCenterOfMass ?? true,
            centerOfMass: item.centerOfMass || { x: 0, y: 0, z: 0 },
            automaticTensor: item.automaticTensor ?? true,
            inertiaTensor: item.inertiaTensor || { x: 1, y: 1, z: 1 },
            inertiaTensorRotation: item.inertiaTensorRotation || {
              x: 0,
              y: 0,
              z: 0,
            },
            useGravity: item.useGravity ?? true,
            isKinematic: item.isKinematic ?? false,
            interpolate: item.interpolate || 'None',
            collisionDetection: item.collisionDetection || 'Discrete',
            freezePosition: item.freezePosition || {
              x: false,
              y: false,
              z: false,
            },
            freezeRotation: item.freezeRotation || {
              x: false,
              y: false,
              z: false,
            },
            includeLayers: item.includeLayers || 'Nothing',
            excludeLayers: item.excludeLayers || 'Nothing',
            colliderType: item.colliderType || 'Box',
            isViewCollider: item.isViewCollider ?? item.is_view_collider ?? true,
            physicsMaterial: material,
          };
        });
      }

      if (worldSettings) {
        state.worldSettings = {
          ...state.worldSettings,
          ...worldSettings,
        };
      }
    },
    restorePhysicsHistory: (state, action) => {
      if (!action.payload) return;
      const { physicsEnabled, worldSettings, physicsObjects } = action.payload;
      if (typeof physicsEnabled === 'boolean') {
        state.physicsEnabled = physicsEnabled;
      }
      if (worldSettings) state.worldSettings = worldSettings;
      if (physicsObjects) state.physicsObjects = physicsObjects;
    },
  },
  extraReducers: (builder) => {
    builder
      // Apply physics to object
      .addCase(applyPhysicsToObject.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(applyPhysicsToObject.fulfilled, (state, action) => {
        state.loading = false;
        const { objectId, properties } = action.payload;
        const existingIndex = state.physicsObjects.findIndex(
          (obj) => obj.objectId === objectId
        );

        if (existingIndex >= 0) {
          state.physicsObjects[existingIndex] = {
            ...state.physicsObjects[existingIndex],
            ...properties,
          };
        } else {
          state.physicsObjects.push({
            objectId,
            ...state.defaultPhysicsProperties,
            ...properties,
          });
        }
      })
      .addCase(applyPhysicsToObject.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message;
      })

      // Remove physics from object
      .addCase(removePhysicsFromObject.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(removePhysicsFromObject.fulfilled, (state, action) => {
        state.loading = false;
        const objectId = action.payload;
        state.physicsObjects = state.physicsObjects.filter(
          (obj) => obj.objectId !== objectId
        );
      })
      .addCase(removePhysicsFromObject.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message;
      })

      // Update physics properties
      .addCase(updatePhysicsProperties.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(updatePhysicsProperties.fulfilled, (state, action) => {
        state.loading = false;
        const { objectId, properties } = action.payload;
        const existingIndex = state.physicsObjects.findIndex(
          (obj) => obj.objectId === objectId
        );

        if (existingIndex >= 0) {
          state.physicsObjects[existingIndex] = {
            ...state.physicsObjects[existingIndex],
            ...properties,
          };
        }
      })
      .addCase(updatePhysicsProperties.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message;
      })

      // Toggle physics world
      .addCase(togglePhysicsWorld.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(togglePhysicsWorld.fulfilled, (state, action) => {
        state.loading = false;
        state.physicsEnabled = action.payload;
      })
      .addCase(togglePhysicsWorld.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message;
      });
  },
});

export const {
  setObjectPhysicsProperties,
  removeObjectPhysics,
  updateWorldSettings,
  setPhysicsEnabled,
  updateDefaultPhysicsProperties,
  addPhysicsMaterial,
  updatePhysicsMaterial,
  setPreviewModePhysicsEnabled,
  setPreviewModePhysicsWorld,
  addPreviewModePhysicsObject,
  removePreviewModePhysicsObject,
  clearPreviewModePhysics,
  clearAllPhysics,
  setPhysicsLoading,
  setPhysicsError,
  loadPhysicsData,
  restorePhysicsHistory,
} = physicsSlice.actions;

export default physicsSlice.reducer;
